import { listTickets } from '@/features/atendimento/api';
import { getSocket } from '@/features/atendimento/socket';
import { useOpenTicketStore } from '@/features/atendimento/stores/open-ticket-store';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

const TITULO = 'ChatPlus';
/** Transferido pra mim: espera a transferência ser gravada antes de perguntar. */
const ESPERA_TRANSFERENCIA_MS = 3_000;

/** "(3) ChatPlus" com N atendimentos e "ChatPlus" sem nada pendente. */
export function tituloDaAba(naoLidos: number): string {
  return naoLidos > 0 ? `(${naoLidos}) ${TITULO}` : TITULO;
}

/**
 * Título da aba do navegador com a quantidade de atendimentos ATIVOS do
 * usuário com mensagem não lida — "(3) ChatPlus", igual ao WhatsApp Web.
 * Pendentes não entram (dono, 21/out/2026).
 *
 * O número reage NA HORA pelos eventos que a tela já recebe (mensagem nova num
 * ativo dele soma; abrir a conversa — nesta ou em outra aba —, fechar ou
 * transferir pra outro tira), sem perguntar nada ao servidor. Ele só é
 * consultado: ao abrir o ChatPlus (ponto de partida), quando a conexão volta
 * (eventos podem ter se perdido) e quando transferem um atendimento PRA ele
 * (o aviso não diz se tem não lida). Perguntar a cada evento gerava consulta
 * à toa e ainda era lento — a pergunta chegava antes de a leitura ser gravada.
 *
 * NÃO lê a lista de Ativos guardada no cache: com a tela de Atendimento
 * fechada ela fica velha, e somava atendimentos que já tinham sido lidos.
 * Abrir a conversa nesta aba já volta como evento `ticket.read` em ms.
 */
export function useTituloDaAba(): void {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);

  const q = useQuery({
    queryKey: ['aba', 'ativos-nao-lidos', userId] as const,
    queryFn: async () => {
      const r = await listTickets('active', { scope: 'mine', onlyUnread: true, limit: 100 });
      return { total: r.total, ids: r.tickets.map((t) => t.id) };
    },
    enabled: userId != null,
    // Ponto de partida SEMPRE do servidor ao abrir (o cache salvo no navegador
    // traria o número da sessão anterior) e nunca mais por conta própria —
    // dali em diante quem atualiza são os eventos.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Quais estão não lidos agora. O servidor manda até 100 ids; passou disso,
  // o excedente entra só na conta (caso raro).
  const [naoLidos, setNaoLidos] = useState<Set<number>>(new Set());
  const [excedente, setExcedente] = useState(0);
  /**
   * TODA resposta do servidor vale — inclusive a igual à anterior.
   *
   * O TanStack devolve o MESMO objeto quando a resposta não mudou, e com só
   * `q.data` como gatilho a correção era ignorada: o conjunto local ganhava um
   * atendimento por evento, o "lida" se perdia numa queda de conexão, a volta
   * perguntava ao servidor ("zero", igual à abertura) e a aba seguia "(1)" até
   * um F5 (srv03, 31/out/2026: conexão caída das 17:08 às 18:04). A hora da
   * resposta muda a cada busca, então ela também dispara.
   */
  const resposta = q.data;
  const respondidaEm = q.dataUpdatedAt;
  useEffect(() => {
    if (!resposta || respondidaEm === 0) return;
    setNaoLidos(new Set(resposta.ids));
    setExcedente(Math.max(0, resposta.total - resposta.ids.length));
  }, [resposta, respondidaEm]);
  const total = naoLidos.size + excedente;

  useEffect(() => {
    document.title = tituloDaAba(total);
  }, [total]);
  useEffect(
    () => () => {
      document.title = TITULO;
    },
    [],
  );

  // Pra os ouvintes (registrados uma vez) enxergarem o conjunto atual.
  const naoLidosRef = useRef(naoLidos);
  naoLidosRef.current = naoLidos;

  useEffect(() => {
    if (userId == null) return;
    const chave = ['aba', 'ativos-nao-lidos', userId];

    let transferencia: ReturnType<typeof setTimeout> | null = null;
    const perguntarAoServidor = (): void => {
      void qc.invalidateQueries({ queryKey: chave });
    };
    // Só muda quando o conjunto mudou de fato — leitura/fechamento de conversa
    // de colega chega aqui e é ignorada.
    const mudar = (fn: (s: Set<number>) => boolean): void => {
      const proximo = new Set(naoLidosRef.current);
      if (!fn(proximo)) return;
      naoLidosRef.current = proximo;
      setNaoLidos(proximo);
    };
    const adicionar = (id: number): void => mudar((s) => !s.has(id) && Boolean(s.add(id)));
    const tirar = (ids: number[]): void =>
      mudar((s) => ids.map((id) => s.delete(id)).some(Boolean));

    const socket = getSocket();
    const onMensagem = (p: {
      ticket_id?: number;
      direction?: string;
      ticket_status?: string;
      user_id?: number | null;
    }): void => {
      if (p.direction === 'outbound' || p.ticket_id === undefined) return;
      if (p.ticket_status !== 'active' || p.user_id !== userId) return;
      // Conversa aberta e visível nesta aba: já está sendo lida.
      const aberta = useOpenTicketStore.getState().openTicketId === p.ticket_id;
      if (aberta && document.visibilityState === 'visible') return;
      adicionar(p.ticket_id);
    };
    const onLidoOuFechado = (p: { ticket_id?: number; ticket_ids?: number[] }): void => {
      tirar([...(p.ticket_id !== undefined ? [p.ticket_id] : []), ...(p.ticket_ids ?? [])]);
    };
    const onTransferido = (p: { ticket_id?: number; to_user_id?: number | null }): void => {
      if (p.ticket_id === undefined) return;
      // Veio pra mim: o aviso não diz se tem não lida — o servidor diz.
      if (p.to_user_id === userId) {
        if (transferencia) clearTimeout(transferencia);
        transferencia = setTimeout(perguntarAoServidor, ESPERA_TRANSFERENCIA_MS);
      } else tirar([p.ticket_id]);
    };
    const onMarcadoNaoLido = (p: { ticket_id?: number; actor_user_id?: number | null }): void => {
      if (p.actor_user_id === userId && p.ticket_id !== undefined) adicionar(p.ticket_id);
    };
    socket.on('message.received', onMensagem);
    socket.on('ticket.read', onLidoOuFechado);
    socket.on('ticket.closed', onLidoOuFechado);
    socket.on('ticket.transferred', onTransferido);
    socket.on('ticket.unread', onMarcadoNaoLido);
    // A primeira conexão é a abertura (o ponto de partida já foi buscado); só
    // RECONEXÃO pergunta — eventos podem ter se perdido enquanto caiu. (O socket
    // do painel roda num Worker e não expõe `connected`.)
    let primeiraConexao = true;
    const onConectar = (): void => {
      if (primeiraConexao) {
        primeiraConexao = false;
        return;
      }
      perguntarAoServidor();
    };
    socket.on('connect', onConectar);
    return () => {
      socket.off('message.received', onMensagem);
      socket.off('ticket.read', onLidoOuFechado);
      socket.off('ticket.closed', onLidoOuFechado);
      socket.off('ticket.transferred', onTransferido);
      socket.off('ticket.unread', onMarcadoNaoLido);
      socket.off('connect', onConectar);
      if (transferencia) clearTimeout(transferencia);
    };
  }, [qc, userId]);
}
